import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLogin } from "../api";

// Zod validation schema
const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login: React.FunctionComponent = () => {
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
  });

  const { mutateAsync: login, isPending, error } = useLogin();

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data);
    } catch (error: any) {
      console.error("Login failed:", error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Coralytics</title>
        <meta
          name="description"
          content="Login to your Coralytics account and access your AI-powered real estate tools."
        />
      </Helmet>

      {/* Background with white */}
      <section
        className="relative min-h-screen flex items-center justify-center py-12"
        style={{
          background: "white",
        }}
      >
        <div className="container relative z-10">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8 col-sm-10">
              <div
                className="card shadow-lg border-0"
                style={{
                  borderRadius: "20px",
                  boxShadow:
                    "0 20px 40px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)",
                }}
              >
                <div className="card-body p-5">
                  {/* Header */}
                  <div className="text-center mb-4">
                    <a className="navbar-brand logo-image" href="/home">
                      <img
                        src="/assets/images/Coralytics trans.webp"
                        alt="alternative"
                        style={{ maxHeight: "60px", margin: "0 auto" }}
                      />
                    </a>
                    <hr
                      className="mt-2 mb-3"
                      style={{
                        borderColor: "rgb(87, 60, 255)",
                        borderWidth: "2px",
                      }}
                    />
                  </div>

                  {/* Login Form */}
                  <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {/* Email Field */}
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label fw-semibold">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control form-control-lg ${
                          errors.email ? "is-invalid" : ""
                        }`}
                        id="email"
                        placeholder="Enter your email address"
                        style={{ borderColor: "#e2e8f0", padding: "14px 16px" }}
                        {...register("email")}
                      />
                      {errors.email && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.email.message}
                        </div>
                      )}
                    </div>

                    {/* Password Field */}
                    <div className="mb-3">
                      <label
                        htmlFor="password"
                        className="form-label fw-semibold"
                      >
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className={`form-control form-control-lg ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          id="password"
                          placeholder="Enter your password"
                          style={{
                            borderColor: "#e2e8f0",
                            padding: "14px 16px",
                            paddingRight: "50px",
                          }}
                          {...register("password")}
                        />
                        <button
                          type="button"
                          className="btn position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            color: "#6c757d",
                            padding: "0",
                            fontSize: "16px",
                          }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i
                            className={`fas ${
                              showPassword ? "fa-eye-slash" : "fa-eye"
                            }`}
                          ></i>
                        </button>
                      </div>
                      {errors.password && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errors.password.message}
                        </div>
                      )}
                    </div>

                    {/* API Error Display */}
                    {error && (
                      <div className="alert alert-danger mb-3" role="alert">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error.message || "Login failed. Please try again."}
                      </div>
                    )}

                    {/* Submit Button */}
                    <div className="d-grid mb-4">
                      <button
                        type="submit"
                        className="btn btn-lg fw-semibold"
                        disabled={isPending}
                        style={{
                          background: "rgb(87, 60, 255)",
                          border: "none",
                          color: "white",
                          borderRadius: "12px",
                          padding: "12px 24px",
                          fontSize: "16px",
                          transition: "all 0.3s ease",
                          transform: isPending ? "scale(0.98)" : "scale(1)",
                          boxShadow: "0 4px 15px rgba(87, 60, 255, 0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "scale(1.02)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(87, 60, 255, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(87, 60, 255, 0.3)";
                        }}
                      >
                        {isPending ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Signing In...
                          </>
                        ) : (
                          <>Sign In</>
                        )}
                      </button>
                    </div>

                    {/* Sign Up Link */}
                    <div className="text-center">
                      <p className="mb-0" style={{ color: "#6c757d" }}>
                        Don't have an account?{" "}
                        <Link
                          to="/sign-up"
                          style={{
                            color: "rgb(87, 60, 255)",
                            textDecoration: "none",
                            fontWeight: "600",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.textDecoration = "none";
                          }}
                        >
                          Sign up here
                        </Link>
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Login;
